import { Component, type ErrorInfo, type ReactNode } from "react";
import { useRouteError } from "react-router";
import { Sentry } from "../../lib/telemetry/sentry";

// ---- Shared fallback UI --------------------------------------------------

interface FallbackProps {
  error: Error | null;
  eventId?: string | null;
}

function Fallback({ error, eventId }: FallbackProps): ReactNode {
  const isDev = import.meta.env.DEV;

  return (
    <div
      data-testid="root-error-boundary"
      className="min-h-screen bg-[#F8FAFC] font-['Inter',sans-serif] flex items-center justify-center p-6"
    >
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <h1 className="text-xl font-semibold text-slate-900 mb-1">
          Something went wrong
        </h1>
        <p className="text-sm text-slate-500 mb-4">
          An unexpected error occurred. Our team has been notified.
        </p>

        {eventId && (
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600 font-mono mb-4">
            Error ID: <span data-testid="root-error-id">{eventId}</span>
          </div>
        )}

        {/*
          Stack traces are only surfaced in dev. In prod we deliberately
          avoid leaking them to end users — the event ID above is enough
          for support to look up the full trace in Sentry.
        */}
        {isDev && error && (
          <pre className="max-h-48 overflow-auto rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 mb-4 whitespace-pre-wrap">
            {error.message}
            {error.stack ? `\n\n${error.stack}` : ""}
          </pre>
        )}

        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="root-error-reload"
            onClick={() => window.location.reload()}
            className="flex-1 px-4 py-2 text-sm font-medium text-white bg-[#0070F2] hover:bg-[#005BCA] rounded-md transition-colors"
          >
            Reload
          </button>
          <a
            data-testid="root-error-home"
            href="/"
            className="flex-1 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-md transition-colors text-center"
          >
            Return home
          </a>
        </div>
      </div>
    </div>
  );
}

// ---- Class-component boundary -------------------------------------------
// Catches errors thrown by the provider tree *above* the router —
// AuthProvider / OrgProvider / Sentry init etc. Route-level errors are
// caught separately by `RouteErrorElement` (react-router has its own
// try/catch around Component rendering and will not bubble those).

interface RootErrorBoundaryProps {
  children: ReactNode;
}

interface RootErrorBoundaryState {
  error: Error | null;
  eventId: string | null;
}

export class RootErrorBoundary extends Component<
  RootErrorBoundaryProps,
  RootErrorBoundaryState
> {
  state: RootErrorBoundaryState = { error: null, eventId: null };

  static getDerivedStateFromError(error: Error): Partial<RootErrorBoundaryState> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    const eventId = Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: info.componentStack ?? "",
        },
      },
    });
    this.setState({ eventId });
  }

  render(): ReactNode {
    const { error, eventId } = this.state;
    if (!error) return this.props.children;
    return <Fallback error={error} eventId={eventId} />;
  }
}

// ---- Route-level error element -----------------------------------------
// Used as the router's `errorElement` — renders the same fallback for
// errors thrown inside route components. We also pipe those into Sentry
// from here so the class boundary and the route boundary share one event
// pipeline.

export function RouteErrorElement(): ReactNode {
  const routeError = useRouteError();
  // react-router wraps thrown values; normalize to an Error so the Fallback
  // can decide whether to show a stack trace.
  const error =
    routeError instanceof Error
      ? routeError
      : new Error(
          typeof routeError === "string" ? routeError : "Unknown route error",
        );

  const eventId = Sentry.captureException(error, {
    contexts: { react: { source: "react-router errorElement" } },
  });

  return <Fallback error={error} eventId={eventId} />;
}
